import { Link } from "react-router-dom";
import { Leaf, Ship, ShieldCheck, Handshake, ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal, SectionHeader } from "../components/Reveal";
import { IMG, REGIONS } from "../data/site";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Uncompromising Quality",
    text: "Every lot is lab-tested against the agreed spec before it ships — and the test reports travel with the goods.",
  },
  {
    icon: Leaf,
    title: "Sustainable Sourcing",
    text: "GOTS-certified textiles, GoodWeave rugs and FSC-option timber. Certified supply chains, not greenwashing.",
  },
  {
    icon: Ship,
    title: "Timely Shipping",
    text: "Honest lead times, proactive updates and cargo-space partnerships with major carriers.",
  },
  {
    icon: Handshake,
    title: "Ethical Fair Trade",
    text: "SEDEX-audited units and direct artisan partnerships that keep value in the craft clusters.",
  },
];

const CHAPTERS = [
  {
    label: "Chapter 01",
    title: "The Beginning",
    text: "NKB Traders starts as a single-commodity exporter — one commodity, a handful of farms, one destination market. The playbook: never ship what you haven't inspected.",
  },
  {
    label: "Chapter 02",
    title: "First International Shipment",
    text: "A trial container becomes a standing quarterly order. Buyers start asking for a second product line — and the sourcing network grows to answer.",
  },
  {
    label: "Chapter 03",
    title: "Multi-Category Expansion",
    text: "Agri & food, textiles, handicrafts and industrial goods under one roof. Mixed-container consolidation turns four supplier relationships into one accountable desk.",
  },
  {
    label: "Chapter 04",
    title: "NKB Today",
    text: "A multi-category export house serving importers across the Middle East, North America, Europe, Asia Pacific and Africa — with quality labs and consolidation hubs doing the heavy lifting.",
  },
];

export default function About() {
  return (
    <div data-testid="page-about" className="bg-mist">
      {/* Header */}
      <section className="relative overflow-hidden pt-[72px]">
        <div className="dot-grid pointer-events-none absolute inset-0" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 lg:px-8 lg:pb-24 lg:pt-24">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-emerald-700">
              <span className="h-[1px] w-8 bg-emerald-700/40" />
              About NKB Traders
            </p>
          </Reveal>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLines
              delay={0.2}
              lines={[
                "A trading house",
                <span key="b" className="italic text-forest">
                  built on trust.
                </span>,
              ]}
            />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              We exist to make importing from India feel like buying locally — one desk, honest
              lead times, certificates that match the cargo.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section data-testid="about-story" className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="overflow-hidden rounded-[2rem] rounded-t-[8rem] border border-slate-200 shadow-[0_40px_80px_-40px_rgba(6,78,59,0.4)]">
              <img
                src={IMG.crane}
                alt="Gantry cranes at an Indian port"
                loading="lazy"
                className="img-editorial h-[440px] w-full object-cover sm:h-[540px]"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <SectionHeader overline="Our Story" title="Built beside the farms, mills and workshops we source from." />
            <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-600">
              <Reveal delay={0.1}>
                <p>
                  NKB Traders began the way most good trade does — with one buyer's problem worth
                  solving: consistent, correctly-graded basmati, on the vessel it was promised for.
                  We solved it by going to the source: standing in mill yards, pulling samples
                  ourselves, and refusing to let paperwork trail the cargo.
                </p>
              </Reveal>
              <Reveal delay={0.18}>
                <p>
                  That discipline became the company. Today we operate across four verticals —
                  agri &amp; food, textiles &amp; garments, handicrafts, and industrial goods —
                  but the method hasn't changed: long-term partners in each production cluster,
                  testing before commitment, and consolidation hubs where mixed cargo becomes one
                  clean, documented shipment.
                </p>
              </Reveal>
              <Reveal delay={0.26}>
                <p>
                  Whether you import two pallets of spices a year or fifty containers of mixed
                  goods, you get the same thing: a single accountable desk that answers within 24
                  hours and a shipment that matches its sample.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="about-values" className="bg-sage/60 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader overline="What We Stand For" title="Four principles, zero exceptions." />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className="h-full">
                <div className="group flex h-full flex-col rounded-3xl border border-forest/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-forest/40 hover:shadow-[0_24px_50px_-24px_rgba(6,78,59,0.3)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest text-white transition-colors group-hover:bg-gold group-hover:text-ink">
                    <v.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-semibold text-ink">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section data-testid="about-timeline" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <SectionHeader overline="The Journey" title="One shipment at a time." />
        <div className="relative mt-14 border-l border-forest/15 pl-8 lg:pl-14">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.06}>
              <div className="relative pb-12 last:pb-0" data-testid={`about-chapter-${i + 1}`}>
                <span className="absolute -left-[41px] top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-gold bg-mist lg:-left-[65px]">
                  <span className="h-2 w-2 rounded-full bg-gold" />
                </span>
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                  {c.label}
                </p>
                <h3 className="mt-2 font-display text-2xl font-semibold text-ink">{c.title}</h3>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
                  {c.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section data-testid="about-gallery" className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28">
        <SectionHeader overline="Inside The Operation" title="Where your cargo actually goes." />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            { src: IMG.factoryPipes, cap: "Quality lab & inspection bays" },
            { src: IMG.portTerminal, cap: "Consolidation & container stuffing" },
            { src: IMG.heroPort, cap: "Gateway ports: JNPT · Mundra · Chennai" },
          ].map((g, i) => (
            <Reveal key={g.cap} delay={i * 0.08}>
              <figure className="group overflow-hidden rounded-3xl border border-slate-200">
                <div className="overflow-hidden">
                  <img
                    src={g.src}
                    alt={g.cap}
                    loading="lazy"
                    className="img-editorial h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <figcaption className="border-t border-slate-100 bg-white px-5 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {g.cap}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Global reach */}
      <section data-testid="about-reach" className="relative overflow-hidden bg-ink py-20 lg:py-28">
        <div className="dot-grid-light pointer-events-none absolute inset-0" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <SectionHeader
              dark
              overline="Global Network"
              title="Where NKB containers land."
            />
            <ul className="mt-10 space-y-4">
              {REGIONS.map((r, i) => (
                <Reveal key={r} delay={i * 0.07}>
                  <li className="flex items-center justify-between border-b border-white/10 pb-4" data-testid={`reach-region-${i}`}>
                    <span className="text-sm font-medium text-white sm:text-base">{r}</span>
                    <ArrowUpRight className="h-4 w-4 text-gold" />
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-[2rem] border border-white/10">
              <img
                src={IMG.portTerminal}
                alt="Container terminal"
                loading="lazy"
                className="img-editorial h-[420px] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-6 lg:px-8">
        <Reveal>
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            Come see what a docs-ready exporter feels like.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/contact"
            data-testid="about-cta-button"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-forest-hover"
          >
            Start an Enquiry
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
